<template>
  <v-dialog
    :value="value"
    @input="$emit('input', $event)"
    :max-width="maxWidth"
    persistent
    :content-class="contentClass"
  >
    <v-card class="confirmation-dialog-card">
      <v-toolbar
        dense
        flat
        :class="toolbarClass"
        :color="headerColor === 'error' ? undefined : headerColor"
      >
        <span class="confirmation-dialog-title">{{ title }}</span>
        <v-spacer />
        <v-btn icon dark @click="onClose">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>
      <v-card-text class="pa-6">
        <p v-if="message && !$slots.default" class="confirmation-dialog-message">
          {{ message }}
        </p>
        <slot v-else />
      </v-card-text>
      <v-card-actions class="pa-6 pt-0 justify-end">
        <v-btn
          outlined
          :class="cancelButtonClass"
          :color="headerColor === 'error' ? undefined : headerColor"
          @click="onCancel"
        >
          {{ displayCancelLabel }}
        </v-btn>
        <v-btn
          :class="confirmButtonClass"
          :color="headerColor === 'error' ? undefined : headerColor"
          :disabled="confirmDisabled"
          dark
          class="ml-3"
          @click="onConfirm"
        >
          {{ displayConfirmLabel }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'ConfirmationDialog',

  props: {
    value: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      required: true,
    },
    /** 'primary' for green header/buttons, 'error' for red */
    headerColor: {
      type: String,
      default: 'primary',
      validator: (v) => ['primary', 'error'].includes(v),
    },
    /** Shown when no default slot is provided */
    message: {
      type: String,
      default: '',
    },
    cancelLabel: {
      type: String,
      default: '',
    },
    confirmLabel: {
      type: String,
      default: '',
    },
    confirmDisabled: {
      type: Boolean,
      default: false,
    },
    maxWidth: {
      type: String,
      default: '500',
    },
    contentClass: {
      type: String,
      default: '',
    },
  },

  computed: {
    toolbarClass() {
      if (this.headerColor === 'error') {
        return 'confirmation-dialog-toolbar-error white--text';
      }
      return 'white--text';
    },
    cancelButtonClass() {
      return this.headerColor === 'error'
        ? 'confirmation-dialog-cancel-error'
        : 'confirmation-dialog-btn-uppercase';
    },
    confirmButtonClass() {
      const base = 'confirmation-dialog-btn-uppercase';
      return this.headerColor === 'error'
        ? `${base} confirmation-dialog-confirm-error`
        : base;
    },
    displayCancelLabel() {
      return this.cancelLabel || this.$t('finance.loanApplications.cancel');
    },
    displayConfirmLabel() {
      return this.confirmLabel || this.$t('finance.loanApplications.confirm');
    },
  },

  methods: {
    onClose() {
      this.$emit('input', false);
      this.$emit('cancel');
    },
    onCancel() {
      this.$emit('input', false);
      this.$emit('cancel');
    },
    onConfirm() {
      this.$emit('confirm');
    },
  },
};
</script>

<style scoped>
.confirmation-dialog-card {
  border-radius: 8px;
  overflow: hidden;
}

.confirmation-dialog-title {
  font-weight: 700;
  font-size: 16px;
}

.confirmation-dialog-message {
  font-size: 14px;
  color: #333;
  margin: 0;
  text-align: center;
}

.confirmation-dialog-btn-uppercase {
  text-transform: uppercase;
  font-weight: 700;
}

/* Error (reject) variant */
.confirmation-dialog-toolbar-error {
  background-color: #f03737 !important;
}

.confirmation-dialog-cancel-error {
  border-color: #f03737 !important;
  color: #f03737 !important;
}

.confirmation-dialog-confirm-error {
  background-color: #f03737 !important;
  color: #fff !important;
}
</style>
